import { useEffect, useState } from 'react'
import { can, type OffboardingGate, type OffboardingItemState } from '../../lib/api'
import {
  exitApi,
  type DirectoryDomains,
  type ExitItemState,
  type LegalHold,
  type ObservedAccount,
  type OffboardingEvidence,
} from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { useSave } from '../../lib/save'
import { ErrorBox } from '../DataTable'
import { ExitEvidenceForm } from '../ExitEvidenceForm'
import { Dialog, ProgressRing, Status, TaskList, Toggletip, type TaskState } from '../kit'
import { useKept } from './hooks'
import { stamp, truncate } from './words'

// Section 17's checklist as the evidence an inspector will ask for.
//
// # WHAT MOVED WHERE
//
// The list this replaces printed, for each of eleven items, what is missing,
// why it is asked, who recorded it and a form, one under another. Here the
// required items are one row each with an owner and a state, the optional
// ones a quieter list below, and everything else (the reason, the evidence
// recorded, the directory's accounts, the form) opens in a drawer from the
// row. Every sentence in the drawer is still the API's own.
//
// # WHO OWNS AN ITEM
//
// The owner column is the stage of the sign-off chain that vouches for the
// item, from GET /v1/exits. Without that read the column is empty rather than
// guessed.

function asItem(i: OffboardingItemState): ExitItemState {
  return i as ExitItemState
}

// The proof in a line, enough to see what kind of thing stands behind a tick.
export function evidenceWords(item: ExitItemState): string {
  const e = item.evidence
  if (!e) return ''
  switch (e.kind) {
    case 'DOCUMENT':
      return 'Document: ' + (e.documentTitle ?? e.documentId ?? '')
    case 'REVOCATION':
      return (e.revoked ?? []).map((r) => r.identifier + ' in ' + r.system + ' on ' + r.revokedOn).join('; ')
    case 'NONE_HELD':
      return (e.statement ?? '') + ' Confirmed by ' + (e.confirmedBy ?? '') + '.'
    case 'NOTICE':
      return 'Notice served on ' + (e.servedOn ?? '') + ' to ' + (e.servedTo ?? '') + ' by ' + (e.method ?? '') + '.'
    case 'RETURN':
      return (
        'Transition completed on ' +
        (e.transitionCompletedOn ?? '') +
        '. ' +
        (e.returned === false ? (e.statement ?? '') : 'Received ' + (e.what ?? '') + ' on ' + (e.receivedOn ?? '') + '.')
      )
    case 'DESTRUCTION':
      return (e.what ?? '') + ' destroyed by ' + (e.method ?? '') + ' on ' + (e.deletedOn ?? '') + ', ' + (e.performedBy ?? '') + '.'
    case 'RETENTION':
      return 'Kept until ' + (e.retainedUntil ?? '') + '. ' + (e.protection ?? '')
    case 'LEGAL_HOLD':
      return 'Kept under legal hold ' + (e.legalHoldId ?? '') + '.'
  }
  return ''
}

function observedNote(item: ExitItemState): string | undefined {
  const o = item.observed
  return o ? o.closed + ' of ' + o.total + ' closed' : undefined
}

function taskState(item: ExitItemState, recordable: boolean): TaskState {
  if (item.done) return 'done'
  if (item.observed) return 'progress'
  if (!recordable) return 'waiting'
  return 'missing'
}

export function ExitEvidencePanel({
  vendorId,
  gate,
  recordable,
  owners,
  holds,
  openKey,
  onOpen,
  onRecorded,
  startedOn,
}: {
  vendorId: string
  gate: OffboardingGate
  recordable: boolean
  owners: Record<string, string>
  holds: LegalHold[] | null
  openKey: string | null
  onOpen: (key: string | null) => void
  onRecorded: (next: OffboardingGate) => void
  // The day the exit was opened, YYYY-MM-DD, where the record says (date
  // rule D22: what the offboarding did is not dated before it began).
  startedOn?: string
}) {
  const items = gate.items.map(asItem)
  const required = items.filter((i) => i.mandatory)
  const optional = items.filter((i) => !i.mandatory)
  const requiredDone = required.filter((i) => i.done).length
  const open = items.find((i) => i.key === openKey) ?? null
  // The drawer keeps showing the item it last opened while it slides away.
  const shown = useKept(open)
  const mayRecord = recordable && can('offboarding.run')

  return (
    <section className="panel exit-panel exit-evidence" aria-labelledby="exit-evidence-h">
      <div className="exit-panel-head">
        <ProgressRing done={requiredDone} total={required.length} label="Required evidence recorded" />
        <div className="exit-grow">
          <h2 id="exit-evidence-h">Evidence</h2>
          <span className="exit-sub">
            {gate.blocking.length === 0 ? 'All required evidence is recorded' : gate.blocking.length + ' required, not yet recorded'}
          </span>
        </div>
        <Toggletip label="About the evidence">
          {'The ' +
            required.length +
            ' required items hold offboarding until each is recorded with its proof. The optional ones hold nothing but are worth recording.'}
        </Toggletip>
      </div>

      <TaskList
        items={required.map((i) => ({
          key: i.key,
          name: i.name,
          owner: owners[i.key],
          state: taskState(i, recordable),
          note: i.evidenceMissing ? 'Needs its evidence again' : observedNote(i),
          action: (
            <button type="button" className="exit-link" onClick={() => onOpen(i.key)}>
              {i.done || !mayRecord ? 'View' : 'Record'}
            </button>
          ),
        }))}
      />

      <h3 className="exit-h3">Optional</h3>
      <ul className="exit-opt">
        {optional.map((i) => (
          <li key={i.key}>
            <span className="exit-opt-name">
              {i.name}
              {owners[i.key] && <span className="exit-opt-owner">{owners[i.key]}</span>}
            </span>
            <Status tone="plain">{i.done ? 'Done' : 'Not recorded'}</Status>
            {(i.done || mayRecord) && (
              <button type="button" className="exit-link" onClick={() => onOpen(i.key)}>
                {i.done || !mayRecord ? 'View' : 'Record'}
              </button>
            )}
          </li>
        ))}
      </ul>

      <DirectoryDomainsLine vendorId={vendorId} />

      <ItemDrawer
        vendorId={vendorId}
        item={shown}
        open={open !== null}
        recordable={mayRecord}
        holds={holds}
        startedOn={startedOn}
        onClose={() => onOpen(null)}
        onRecorded={(next) => {
          onRecorded(next)
          onOpen(null)
        }}
      />
    </section>
  )
}

function ItemDrawer({
  vendorId,
  item,
  open,
  recordable,
  holds,
  startedOn,
  onClose,
  onRecorded,
}: {
  vendorId: string
  item: ExitItemState | null
  open: boolean
  recordable: boolean
  holds: LegalHold[] | null
  startedOn?: string
  onClose: () => void
  onRecorded: (next: OffboardingGate) => void
}) {
  const [note, setNote] = useState('')
  const [evidence, setEvidence] = useState<OffboardingEvidence | null>(null)
  const save = useSave(open && item ? item.key : null, item?.name ?? 'the offboarding item')
  const busy = save.busy
  const [err, setErr] = useState<unknown>(null)
  const names = usePeopleNames()

  // A fresh form each time a row is opened, starting from what was recorded.
  useEffect(() => {
    if (!open) return
    setNote(item?.note ?? '')
    setEvidence(null)
    setErr(null)
  }, [open, item?.key, item?.note])

  function record(done: boolean) {
    if (!item) return
    setErr(null)
    save.send(exitApi.recordItem(vendorId, item.key, { done, note, evidence: done ? (evidence ?? undefined) : undefined }), {
      done: (res) => onRecorded(res.offboarding),
      failed: setErr,
    })
  }


  const needsEvidence = !!item && item.mandatory && evidence === null
  const acts = item !== null && recordable
  const takeBack = acts && item.done

  return (
    <Dialog
      shape="drawer"
      kind="form"
      open={open}
      onClose={onClose}
      title={item?.name ?? ''}
      busy={busy}
      primary={
        !acts
          ? undefined
          : takeBack
            ? {
                label: busy ? 'Recording' : 'Take this back',
                tone: 'plain',
                off: note.trim() === '' && 'Needs a note saying what is still wanted',
                onClick: () => record(false),
                actionId: 'tprm.exit-workspace,vendor,vendor-record.a02',
              }
            : {
                label: busy ? 'Recording' : 'Record as done',
                off: needsEvidence && 'Required, so it is recorded with evidence',
                onClick: () => record(true),
                actionId: 'tprm.exit-workspace,vendor,vendor-record.a02',
              }
      }
    >
      {item && (
        <div className="exit-drawer-stack">
          <div className="exit-row">
            {item.done ? (
              <Status tone="ok">Recorded</Status>
            ) : item.mandatory ? (
              <Status tone="crit">Required</Status>
            ) : (
              <Status tone="neutral">Optional</Status>
            )}
            {item.recordedBy && (
              <span className="exit-sub">
                {(item.done ? 'By ' : 'Taken back by ') + personLabel(names, item.recordedBy)}
                {item.recordedAt ? ', ' + stamp(item.recordedAt) : ''}
              </span>
            )}
          </div>

          {!item.done && <p className="exit-para">{item.outstanding}</p>}
          {item.evidenceMissing && <p className="exit-para exit-warn">{item.evidenceMissing}</p>}
          {item.evidence && (
            <div>
              <h3 className="exit-h3">Evidence</h3>
              <p className="exit-para">{evidenceWords(item)}</p>
              {item.evidenceSha256 && <p className="exit-mono">{item.evidenceSha256}</p>}
            </div>
          )}
          {item.done && item.note && (
            <div>
              <h3 className="exit-h3">Note</h3>
              <p className="exit-para">{item.note}</p>
            </div>
          )}
          {item.done && !item.note && !item.evidence && !item.observed && (
            <p className="exit-sub">Recorded with no note, so nothing says what was done</p>
          )}

          {item.observed && <ObservedAccounts observed={item.observed} />}

          <details className="exit-why">
            <summary>Why this is asked</summary>
            <p className="exit-para">{item.why}</p>
          </details>

          {recordable && (
            <div>
              {err != null && <ErrorBox error={err} what="That item" verb="recorded" />}
              <div className="field">
                <label htmlFor={'off-note-' + item.key}>{item.done ? 'Why it is taken back' : 'Note, optional'}</label>
                <textarea id={'off-note-' + item.key} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              {!item.done && (
                <ExitEvidenceForm
                  vendorId={vendorId}
                  itemKey={item.key}
                  accepts={item.accepts ?? []}
                  value={evidence}
                  onChange={setEvidence}
                  disabled={busy}
                  holds={holds ?? undefined}
                  startedOn={startedOn}
                />
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}

function accountWhen(a: ObservedAccount): string {
  if (a.disabledAt) return stamp(a.disabledAt)
  if (a.removedAt) return 'Removed ' + stamp(a.removedAt)
  return ''
}

// What Microsoft Entra ID showed for this vendor's accounts during the exit.
function ObservedAccounts({ observed }: { observed: NonNullable<ExitItemState['observed']> }) {
  return (
    <div>
      <div className="exit-row">
        <h3 className="exit-h3">Directory accounts</h3>
        <Status tone={observed.met ? 'ok' : 'warn'}>
          {observed.closed} of {observed.total} closed
        </Status>
      </div>
      <p className="exit-sub">From Microsoft Entra ID, read {stamp(observed.observedAt)}</p>
      {observed.accounts.length > 0 && (
        <div className="exit-table-wrap">
          <table className="exit-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>State</th>
                <th>Closed by</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {observed.accounts.map((a) => (
                <tr key={a.upn}>
                  <td>
                    <span className="exit-mono">{a.upn}</span>
                    {a.displayName && <span className="exit-cell-sub">{a.displayName}</span>}
                  </td>
                  <td>
                    <Status tone={a.closed ? 'ok' : 'crit'}>{a.closed ? 'Closed' : 'Open'}</Status>
                  </td>
                  <td>{a.disabledBy ?? <span className="exit-sub">Not recorded</span>}</td>
                  <td className="exit-mono">{accountWhen(a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Where the vendor's accounts are looked for in the customer's directory. Read
// quietly: an API without the directory read, or a person without the
// register, simply does not see the line.
function DirectoryDomainsLine({ vendorId }: { vendorId: string }) {
  const [dd, setDd] = useState<DirectoryDomains | null>(null)
  const [open, setOpen] = useState(false)
  const [domains, setDomains] = useState('')
  const [group, setGroup] = useState('')
  const saving = useSave(open, 'Vendor domains')
  const busy = saving.busy
  const [err, setErr] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    exitApi
      .directoryDomains(vendorId)
      .then((r) => {
        if (live) setDd(r)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [vendorId])

  if (!dd) return null
  const mayEdit = can('offboarding.run')

  function start() {
    setDomains((dd?.domains ?? []).join(', '))
    setGroup(dd?.groupId ?? '')
    setErr(null)
    setOpen(true)
  }

  function save() {
    setErr(null)
    const list = domains
      .split(/[\s,]+/)
      .map((d) => d.trim())
      .filter(Boolean)
    saving.send(exitApi.saveDirectoryDomains(vendorId, { domains: list, groupId: group.trim() }), {
      done: (r) => {
        setDd(r)
        setOpen(false)
      },
      failed: setErr,
    })
  }


  return (
    <div className="exit-domains">
      <span className="exit-sub">Vendor domains</span>
      <span className="exit-domains-list">{dd.domains.length > 0 ? dd.domains.join(', ') : 'None set'}</span>
      <Status tone={dd.collecting ? 'ok' : 'plain'}>{dd.collecting ? 'Checking' : 'Not checking'}</Status>
      {dd.note && <Toggletip label="About vendor domains">{truncate(dd.note, 200)}</Toggletip>}
      {mayEdit && (
        <button type="button" className="exit-link" onClick={start}>
          Set
        </button>
      )}
      <Dialog
        shape="modal"
        kind="form"
        open={open}
        onClose={() => setOpen(false)}
        title="Vendor domains"
        busy={busy}
        primary={{ label: busy ? 'Saving' : 'Save', onClick: save, actionId: 'tprm.exit-workspace,vendor,vendor-record.a03' }}
      >
        <div className="exit-drawer-stack">
          <p className="exit-sub">Needs Entra ID connected with offboarding proof on</p>
          {err != null && <ErrorBox error={err} what="The domains" verb="saved" />}
          <div className="field">
            <label htmlFor="dir-domains">Mail domains</label>
            <input id="dir-domains" type="text" value={domains} placeholder="vendor.example, vendor.co.in" onChange={(e) => setDomains(e.target.value)} />
            <p className="field-note">Guest accounts on these domains are the vendor's</p>
          </div>
          <div className="field">
            <label htmlFor="dir-group">Directory group id, optional</label>
            <input id="dir-group" type="text" value={group} onChange={(e) => setGroup(e.target.value)} />
            <p className="field-note">Members of this group count as the vendor's too</p>
          </div>
          {dd.domains.length > 0 && <p className="exit-sub">Last saved list: {dd.domains.join(', ')}</p>}
        </div>
      </Dialog>
    </div>
  )
}

